// src/utils/push.js
// Push notifications on this device (3.52.1; backend lib/push.js). Uses the
// installed app's background helper (public/sw.js), so it only works on the
// live portal (not npm start). On iPhone and iPad only the installed app
// can get them (Apple's rule): there it says to install the app first.
import { API_BASE } from '../firebase/config';
import { isInstalledApp } from '../hooks/useInstallApp';

const isIos = () => /iPhone|iPad|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
export const pushSupported = () => typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
// iPhone/iPad in the browser: install the app first
export const pushNeedsInstall = () => typeof window !== 'undefined' && isIos() && !isInstalledApp();
export const pushBlocked = () => typeof Notification !== 'undefined' && Notification.permission === 'denied';

// A plain name for this device, shown in the list of devices
export function deviceLabel() {
  const ua = navigator.userAgent;
  const what = /iPhone/.test(ua) ? 'iPhone' : /iPad/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1) ? 'iPad'
    : /Android/.test(ua) ? (/Mobile/.test(ua) ? 'Android phone' : 'Android tablet')
    : /Windows/.test(ua) ? 'Windows computer' : /Mac OS X|Macintosh/.test(ua) ? 'Mac' : /CrOS/.test(ua) ? 'Chromebook' : 'Computer';
  const browser = isInstalledApp() ? 'app' : /Edg\//.test(ua) ? 'Edge' : /Firefox\//.test(ua) ? 'Firefox' : /Chrome\//.test(ua) ? 'Chrome' : /Safari\//.test(ua) ? 'Safari' : 'browser';
  return `${what} (${browser})`;
}

async function registration() {
  const reg = await navigator.serviceWorker.getRegistration(`${process.env.PUBLIC_URL}/`);
  if (!reg) throw new Error('Notifications work on the live Fax Portal. Reload the page and try again.');
  return navigator.serviceWorker.ready;
}
export async function currentSubscription() {
  if (!pushSupported()) return null;
  try { return await (await registration()).pushManager.getSubscription(); } catch { return null; }
}

function keyBytes(base64) {
  const pad = '='.repeat((4 - (base64.length % 4)) % 4);
  const raw = atob((base64 + pad).replace(/-/g, '+').replace(/_/g, '/'));
  return Uint8Array.from(raw, c => c.charCodeAt(0));
}
async function call(getToken, path, method = 'GET', body) {
  const r = await fetch(`${API_BASE}/portal/push/${path}`, { method, headers: { Authorization: `Bearer ${await getToken()}`, 'Content-Type': 'application/json' }, body: body ? JSON.stringify(body) : undefined });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(j.error || 'That didn’t work. Please try again.');
  return j;
}

// Ask the device's permission, then register this device with the portal
export async function turnOn(getToken) {
  if (!pushSupported()) throw new Error("This browser can't show notifications.");
  if (pushNeedsInstall()) throw new Error('On iPhone and iPad, install the app first (Help → Install the app), then turn them on from the app.');
  const perm = await Notification.requestPermission();
  if (perm !== 'granted') throw new Error(perm === 'denied'
    ? 'Notifications are blocked for the Fax Portal on this device. Allow them in the device’s or browser’s settings, then try again.'
    : 'Notifications weren’t allowed.');
  const reg = await registration();
  const { publicKey } = await call(getToken, 'key');
  // 3.86.4 (owner): always a fresh one. An old one kept by the browser can be dead,
  // and the first notification then drops this device without a word.
  const old = await reg.pushManager.getSubscription();
  if (old) await old.unsubscribe().catch(() => {});
  const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes(publicKey) });
  await call(getToken, 'subscribe', 'POST', { subscription: sub.toJSON(), device: deviceLabel() });
  return sub;
}
export async function turnOff(getToken) {
  const sub = await currentSubscription();
  if (!sub) return;
  await call(getToken, 'unsubscribe', 'POST', { endpoint: sub.endpoint }).catch(() => {});
  await sub.unsubscribe().catch(() => {});
}
export const devices = getToken => call(getToken, 'devices');
export const sendTest = getToken => call(getToken, 'test', 'POST');
